/**
 * User Realtime API client.
 *
 * Wraps the backend aggregator + the underlying attendance-today and
 * location-latest endpoints. All ID fields are `SnowflakeId` (string). The
 * shared axios instance (`./axios-config`) handles auth, snowflake-safe JSON,
 * and `{code, data, msg}` unwrapping.
 */

import axios from "./axios-config";
import type { SnowflakeId } from "@/types/snowflake";
import { addDays, dayKeyOf, dayRangeUtc, todayKey } from "@/utils/businessDay";
import { fetchAllPages } from "@/features/task-calendar/fetchAllPages";
import type {
  AttendanceTodayEntry,
  BBox,
  LocationLatestEntry,
  LocationPoint,
  NearbyBBoxParams,
  NearbyRadiusParams,
  NearbyRegionsParams,
  NearbyUser,
  UserRealtimeOutput,
} from "@/types/userRealtime";

const MAX_BATCH = 100; // backend limit for batch endpoints

function chunked<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

// ==================== Aggregator: realtime/users/* ====================

export async function fetchUserRealtime(
  userId: SnowflakeId,
): Promise<UserRealtimeOutput> {
  return axios.get<any, UserRealtimeOutput>(`/pms/realtime/users/${userId}`);
}

export async function fetchUserRealtimeBatch(
  userIds: SnowflakeId[],
): Promise<Record<string, UserRealtimeOutput>> {
  if (userIds.length === 0) return {};
  const merged: Record<string, UserRealtimeOutput> = {};
  for (const slice of chunked(userIds, MAX_BATCH)) {
    const resp = await axios.post<
      any,
      { results: Record<string, UserRealtimeOutput> }
    >(`/pms/realtime/users/batch`, { user_ids: slice });
    Object.assign(merged, resp.results ?? {});
  }
  return merged;
}

export async function fetchNearbyUsers(
  params: NearbyRadiusParams,
): Promise<NearbyUser[]> {
  const body: Record<string, unknown> = {
    lat: params.lat,
    lng: params.lng,
    radius_m: params.radiusM,
    limit: params.limit ?? 50,
  };
  const resp = await axios.post<any, { users: NearbyUser[] }>(
    `/pms/realtime/users/nearby`,
    body,
  );
  return resp.users ?? [];
}

export async function fetchUsersInViewport(
  params: NearbyBBoxParams,
): Promise<NearbyUser[]> {
  const body: Record<string, unknown> = {
    bbox: params.bbox,
    limit: params.limit ?? 100,
  };
  // bbox mode must echo a "center" too for the backend center-relative distance;
  // we use the geometric center of the box as a reasonable default.
  const { sw_lat, sw_lng, ne_lat, ne_lng } = params.bbox;
  body.lat = (sw_lat + ne_lat) / 2;
  body.lng = (sw_lng + ne_lng) / 2;
  const resp = await axios.post<any, { users: NearbyUser[] }>(
    `/pms/realtime/users/nearby`,
    body,
  );
  return resp.users ?? [];
}

export async function fetchUsersInRegions(
  params: NearbyRegionsParams,
): Promise<NearbyUser[]> {
  if (params.regionIds.length === 0) return [];
  if (params.regionIds.length > 10) {
    throw new Error("region_ids 不可超过 10");
  }
  const resp = await axios.post<any, { users: NearbyUser[] }>(
    `/pms/realtime/users/nearby`,
    {
      region_ids: params.regionIds,
      limit: params.limit ?? 50,
    },
  );
  return resp.users ?? [];
}

// ==================== Attendance today ====================

export async function fetchAttendanceToday(
  userId: SnowflakeId,
  fresh = false,
): Promise<AttendanceTodayEntry | null> {
  return axios.get<any, AttendanceTodayEntry | null>(
    `/pms/attendance/today/${userId}`,
    fresh ? { params: { fresh: true } } : undefined,
  );
}

export async function fetchAttendanceTodayBatch(
  userIds: SnowflakeId[],
): Promise<Record<string, AttendanceTodayEntry | null>> {
  if (userIds.length === 0) return {};
  const merged: Record<string, AttendanceTodayEntry | null> = {};
  for (const slice of chunked(userIds, MAX_BATCH)) {
    const resp = await axios.post<
      any,
      { results: Record<string, AttendanceTodayEntry | null> }
    >(`/pms/attendance/today/batch`, { user_ids: slice });
    Object.assign(merged, resp.results ?? {});
  }
  return merged;
}

// ==================== Location latest / history ====================

export async function fetchLatestLocation(
  userId: SnowflakeId,
): Promise<LocationLatestEntry | null> {
  return axios.get<any, LocationLatestEntry | null>(
    `/pms/location-tracking/latest/${userId}`,
  );
}

export async function fetchLatestLocationBatch(
  userIds: SnowflakeId[],
): Promise<Record<string, LocationLatestEntry | null>> {
  if (userIds.length === 0) return {};
  const merged: Record<string, LocationLatestEntry | null> = {};
  for (const slice of chunked(userIds, MAX_BATCH)) {
    const resp = await axios.post<
      any,
      { results: Record<string, LocationLatestEntry | null> }
    >(`/pms/location-tracking/latest/batch`, { user_ids: slice });
    Object.assign(merged, resp.results ?? {});
  }
  return merged;
}

/** Fetch a single user's full GPS track for a given UTC date — uses the
 * pre-existing /history endpoint. */
export async function fetchUserTrack(
  userId: SnowflakeId,
  date: string, // YYYY-MM-DD UTC
): Promise<LocationPoint[]> {
  const url =
    `/pms/location-tracking/history?userIds=${userId}` +
    `&startDate=${date}&endDate=${date}&pageSize=200`;
  const resp = await axios.get<any, { results?: LocationPoint[] }>(url);
  const rows = (resp?.results ?? []) as LocationPoint[];
  // history endpoint returns desc by ts — flip to ascending for polyline drawing
  return rows.slice().reverse();
}

/** `/history` 单页上限（后端 LocationTrackingHotQuery.page_size le=200）。 */
const TRACK_PAGE_SIZE = 200;
/** `/history` 只认 [today_utc - 13, today_utc] 的 UTC 日（Redis 热窗口），越界整单 422。 */
const HOT_WINDOW_DAYS = 13;

interface TrackPage {
  results?: LocationPoint[];
  pagination?: { totalPages?: number };
}

/**
 * 某员工在**用户时区这一天**的轨迹。
 *
 * - `/history` 读 Redis 热存，只收 UTC 日期（不收时刻，也没有 DSL），用户的一天横跨两个 UTC 日 ——
 *   两个 UTC 日都取，再在前端按 [当天 0 点, 次日 0 点) 精确收窄。
 * - 结果按时间倒序分页，窗口外的点也占名额：**必须翻完所有页**，否则一天点多时会截掉当天最早的轨迹。
 * - UTC 日期夹到后端热窗口内：美东白天（UTC 还没到次日）时，当天的 endDate 是「UTC 明天」，
 *   后端校验 `endDate ≤ today_utc` 会整单 422；未来的 UTC 日本来也不可能有点，夹掉不丢数据。
 */
export async function fetchUserTrackForDay(
  userId: SnowflakeId,
  day: string, // YYYY-MM-DD，用户时区
  tz: string,
  now: Date = new Date(),
): Promise<LocationPoint[]> {
  const { startIso, endIso } = dayRangeUtc(day, tz);
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();

  const todayUtc = todayKey("UTC", now);
  const oldestUtc = addDays(todayUtc, -HOT_WINDOW_DAYS);
  const firstUtcDay = dayKeyOf(startIso, "UTC");
  const lastUtcDay = dayKeyOf(new Date(end - 1), "UTC");
  // YYYY-MM-DD 字符串可直接按字典序比较
  const startDate = firstUtcDay < oldestUtc ? oldestUtc : firstUtcDay;
  const endDate = lastUtcDay > todayUtc ? todayUtc : lastUtcDay;
  if (startDate > endDate) return [];

  const all = await fetchAllPages<LocationPoint>(async (page) => {
    const resp = await axios.get<any, TrackPage>(
      `/pms/location-tracking/history?userIds=${userId}` +
        `&startDate=${startDate}&endDate=${endDate}` +
        `&page=${page}&pageSize=${TRACK_PAGE_SIZE}`,
    );
    return {
      results: resp?.results ?? [],
      pagination: {
        page,
        pageSize: TRACK_PAGE_SIZE,
        totalPages: resp?.pagination?.totalPages ?? 1,
        totalCount: 0,
      },
    };
  });

  // 翻页期间有新点写进来（倒序表头插入）会让上一页末尾的点在下一页重复出现 —— 去重
  const seen = new Set<string>();
  const inDay: { p: LocationPoint; ts: number }[] = [];
  for (const p of all) {
    const ts = p.recordedAt ? new Date(p.recordedAt).getTime() : NaN;
    if (!(ts >= start && ts < end)) continue;
    const key = `${p.recordedAt}|${p.latitude}|${p.longitude}|${p.eventType}`;
    if (seen.has(key)) continue;
    seen.add(key);
    inDay.push({ p, ts });
  }
  // 画折线要升序
  return inDay.sort((a, b) => a.ts - b.ts).map((x) => x.p);
}

// Re-export types for callers
export type {
  AttendanceTodayEntry,
  BBox,
  LocationLatestEntry,
  LocationPoint,
  NearbyUser,
  UserRealtimeOutput,
};
